import { useMutation } from '@apollo/client';
import { FormProvider } from 'react-hook-form';
import { toast } from 'react-toastify';
import { z } from 'zod';
import { graphql } from '~/apollo/generated/v4';
import type { SupplementalDocumentPartsFragment } from '~/apollo/generated/v4/graphql';
import { Button } from '~/components/ui/button';
import { HookFormErrors } from '~/components/ui/forms/HookFormErrors';
import { TextInput } from '~/components/ui/forms/text-input';
import { useZodForm } from '~/utils/forms';
import { ppStringOrNull } from '~/utils/zod';

const UPDATE_SUPPLEMENTAL_DOCUMENT = graphql(`
  mutation UpdateSupplementalDocument(
    $id: ID!
    $input: SupplementalDocumentUpdateInput!
  ) {
    supplementalDocumentUpdate(id: $id, input: $input) {
      result {
        ...supplementalDocumentParts
      }
    }
  }
`);

const schema = z.object({
  note: z.preprocess(ppStringOrNull, z.string().nullish()),
});

function initialValues(sd: SupplementalDocumentPartsFragment) {
  return {
    note: sd.note?.trim() ?? '',
  };
}

export function UpdateSupplementalDocumentForm({
  supplementalDocument,
  onUpdateSuccess,
}: {
  supplementalDocument: SupplementalDocumentPartsFragment;
  onUpdateSuccess: () => void;
}) {
  const [updateSuppDoc, { loading, error }] = useMutation(
    UPDATE_SUPPLEMENTAL_DOCUMENT,
  );

  const form = useZodForm({
    schema,
    values: initialValues(supplementalDocument),
  });

  const handleSubmit = form.handleSubmit(async values => {
    try {
      await updateSuppDoc({
        variables: {
          id: supplementalDocument.id,
          input: values,
        },
      });
      toast.success('Supplemental Document updated successfully.');
      onUpdateSuccess();
    } catch (err) {
      console.log('Error saving supp doc', err);
      toast.error(
        'There was a problem saving the changes, check the console for more information.',
      );
    }
  });

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit}>
        <div className="space-y-4">
          <TextInput {...form.register('note')} label="Note" />

          <HookFormErrors graphQLError={error} />

          <div className="text-center">
            <Button type="submit" color="primary" size="sm" loading={loading}>
              Save
            </Button>
          </div>
        </div>
      </form>
    </FormProvider>
  );
}
